import { DEFAULT_CIP_CHEMICALS } from "../../constants/global.constants";
const getAcidMass = (mass) => (mass === "kg" ? 4032000 : 8870400);
const getAlkaliMass = (mass) => (mass === "kg" ? 7200000 : 15840000);
const getMaxMass = (mass) => (mass === "kg" ? 100 : 220);
const getMinTemp = (temperature) => (temperature === "C" ? 20 : 68);
const getMaxTemp = (temperature) => (temperature === "C" ? 100 : 212);
const DEFAULT_BASE_PRICE_RANGE = { min: 0.01, max: 1000 };
const KG_TO_LB_FACTOR = 2.20462;

export const Default_CIP_INFORMATION = (temperature, mass) => {
  return {
    acid: {
      acid_used_per_cip: {
        key: "acid_used_per_cip",
        ranges: {
          hard: {
            min: 0.01,
            max: getAcidMass(mass),
          },
        },
        unit: mass,
      },
      acid_cip_ph: {
        key: "acid_cip_ph",
        ranges: { hard: { min: 1, max: 7 } },
        unit: "pH",
      },
      acid_cip_temp: {
        key: "acid_cip_temp",
        ranges: {
          hard: {
            min: getMinTemp(temperature),
            max: getMaxTemp(temperature),
          },
        },
        unit: `\u00B0${temperature}`,
      },
      acid_cip_duration: {
        key: "acid_cip_duration",
        ranges: { hard: { min: 1, max: 48 } },
        unit: "hrs",
      },
    },
    alkali: {
      alkali_used_per_cip: {
        key: "alkali_used_per_cip",
        ranges: {
          hard: {
            min: 0.01,
            max: getAlkaliMass(mass),
          },
        },
        unit: mass,
      },
      alkali_cip_ph: {
        key: "alkali_cip_ph",
        ranges: { hard: { min: 7, max: 14 } },
        unit: "pH",
      },
      alkali_cip_temp: {
        key: "alkali_cip_temp",
        ranges: {
          hard: {
            min: getMinTemp(temperature),
            max: getMaxTemp(temperature),
          },
        },
        unit: `\u00B0${temperature}`,
      },
      alkali_cip_duration: {
        key: "alkali_cip_duration",
        ranges: { hard: { min: 1, max: 48 } },
        unit: "hrs",
      },
    },
    cip_additive: {
      additional_chemical_per_cip: {
        key: "additional_chemical_per_cip",
        ranges: {
          hard: {
            min: 0.01,
            max: getMaxMass(mass),
          },
        },
        unit: mass,
      },
      cip_solution_ph: {
        key: "cip_solution_ph",
        labelKey: "cip_additive_ph",
        ranges: { hard: { min: 1, max: 14 } },
        unit: "pH",
      },
      cip_solution_temp: {
        key: "cip_solution_temp",
        labelKey: "cip_additive_temp",
        ranges: {
          hard: {
            min: getMinTemp(temperature),
            max: getMaxTemp(temperature),
          },
        },
        unit: `°${temperature}`,
      },
      cip_solution_duration: {
        key: "cip_solution_duration",
        labelKey: "cip_additive_duration",
        ranges: { hard: { min: 1, max: 48 } },
        unit: "hrs",
      },
    },
  };
};

export const CIP_SECTION_META = {
  acid: {
    titleKey: "defaultInformationStrings.acid_cip_ph",
    chemicalLabelKey: "defaultInformationStrings.acidchemicalLabel",
    priceKey: "acid_price",
    priceRange: { min: 0.01, max: 1000 },
    chemicalKey: "acid_chem_id",
    defaultChemicalDisplayName: DEFAULT_CIP_CHEMICALS.ACID.chemical_name,
  },
  alkali: {
    titleKey: "defaultInformationStrings.alkali_cip_ph",
    chemicalLabelKey: "defaultInformationStrings.alkalichemicalLabel",
    priceKey: "alkali_price",
    priceRange: { min: 0.01, max: 1000 },
    chemicalKey: "alkali_chem_id",
    defaultChemicalDisplayName: DEFAULT_CIP_CHEMICALS.ALKALI.chemical_name,
  },
  cip_additive: {
    titleKey: "defaultInformationStrings.cip_additive_ph",
    chemicalLabelKey: "defaultInformationStrings.additiveChemicalLabel",
    priceKey: "additional_chemical_price",
    priceRange: { min: 0.01, max: 1000 },
    chemicalKey: "additional_chemical_chem_id",
    defaultChemicalDisplayName: DEFAULT_CIP_CHEMICALS.CIP_ADDITIVE.chemical_name,
  },
};

export const DEFAULT_CIP_SECTION_KEYS = Object.keys(CIP_SECTION_META);

export const getCIPPriceRangeByCategory = (
  category,
  currency,
  mass,
  conversionRate,
) => {
  const {
    min = DEFAULT_BASE_PRICE_RANGE.min,
    max = DEFAULT_BASE_PRICE_RANGE.max,
  } = CIP_SECTION_META?.[category]?.priceRange || {};

  let rangeMin = Number(min);
  let rangeMax = Number(max);

  if (Number.isNaN(rangeMin) || Number.isNaN(rangeMax)) {
    rangeMin = DEFAULT_BASE_PRICE_RANGE.min;
    rangeMax = DEFAULT_BASE_PRICE_RANGE.max;
  }

  if (mass === "lb") {
    rangeMin *= KG_TO_LB_FACTOR;
    rangeMax *= KG_TO_LB_FACTOR;
  }

  const parsedRate = Number(conversionRate);
  if (currency !== "USD" && !Number.isNaN(parsedRate) && parsedRate > 0) {
    rangeMin *= parsedRate;
    rangeMax *= parsedRate;
  }

  return {
    min: Number(rangeMin.toFixed(2)),
    max: Number(rangeMax.toFixed(2)),
  };
};

export const getCIPPriceRanges = (
  currency,
  mass,
  conversionRate,
  categories = DEFAULT_CIP_SECTION_KEYS,
) =>
  categories.reduce((acc, category) => {
    acc[category] = getCIPPriceRangeByCategory(
      category,
      currency,
      mass,
      conversionRate,
    );
    return acc;
  }, {});

export const PLANT_DETAILS = () => {
  return {
    add: {
      add_info: {
        key: "add_info",
        multiline: "2",
        maxChar: 500,
      },
    },
  };
};

export const INITIAL_DEFAULT_CIP_ERROR_STATE = {
  acid_price: null,
  alkali_price: null,
  additional_chemical_price: null,
  acid_used_per_cip: null,
  alkali_used_per_cip: null,
  additional_chemical_per_cip: null,
  acid_cip_ph: null,
  alkali_cip_ph: null,
  cip_solution_ph: null,
  acid_cip_temp: null,
  alkali_cip_temp: null,
  cip_solution_temp: null,
  acid_cip_duration: null,
  alkali_cip_duration: null,
  cip_solution_duration: null,
};
